// ===================================================================
// ProgressionPage.tsx — "Ma progression" — style sentier kama
// ===================================================================
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from './ThemeContext';
import FloatingBackButton from './FloatingBackButton';
import type { LanguageCode, UserProgress } from '../types/types';
import './ProgressionPage.css';
import { getLevel } from '../data/levelTitles';
import { getWordTranslation } from '../utils/localizedContent';

import { EmojiText } from './common/Picto';
function getTotalXP(codes: LanguageCode[]): number {
  if (codes.length === 0) {
    const allCodes: LanguageCode[] = ['wf', 'ba', 'la', 'ff', 'co', 'sw', 'ka', 'yo'];
    return allCodes.reduce((s, c) => s + parseInt(localStorage.getItem(`${c}-totalXP`) || '0', 10), 0);
  }
  return codes.reduce((s, c) => s + parseInt(localStorage.getItem(`${c}-totalXP`) || '0', 10), 0);
}

function getDailyStreak(): number {
  try {
    const raw = localStorage.getItem('jogolinga-daily-streak');
    if (!raw) return 0;
    return JSON.parse(raw).streak ?? 0;
  } catch { return 0; }
}

// ── Hieroglyph symbols for heatmap cells ────────────────────────────
const HEAT_SYMS = ['', '···', '++++', '||||', '∿∿∿', '<><>', '◆◆◆'];
function heatSym(intensity: number): string { return HEAT_SYMS[Math.min(intensity, 6)]; }

const LANG_NAMES: Record<string, string> = {
  wf: 'Wolof', ba: 'Bambara', la: 'Lingala', sw: 'Swahili', ff: 'Peul', co: 'Copte', yo: 'Yoruba',
};

// ── Build 5-week activity grid ───────────────────────────────────────
function buildActivityGrid(languageCode: LanguageCode): { date: Date; intensity: number }[][] {
  // Read daily activity from localStorage key `${languageCode}-daily-activity`
  // Format: { [dateStr YYYY-MM-DD]: number (words learned/reviewed) }
  let activityMap: Record<string, number> = {};
  try {
    const raw = localStorage.getItem(`${languageCode}-daily-activity`);
    if (raw) activityMap = JSON.parse(raw);
  } catch {}

  // Fallback: days in the streak history (synced with Supabase) show at least
  // the lowest intensity, so activity recorded before the counts existed — or
  // on another device — still appears.
  let activeDays = new Set<string>();
  try {
    const hist = JSON.parse(localStorage.getItem('jogolinga-activity-history') || '[]');
    if (Array.isArray(hist)) activeDays = new Set(hist.map(String));
  } catch {}

  // Build 5 weeks × 7 days ending today
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Find Monday 5 weeks ago
  const dayOfWeek = today.getDay(); // 0=Sun
  const mondayOffset = (dayOfWeek + 6) % 7; // days since Monday
  const start = new Date(today);
  start.setDate(start.getDate() - mondayOffset - 28); // 5 full weeks

  const weeks: { date: Date; intensity: number }[][] = [];
  for (let w = 0; w < 5; w++) {
    const week: { date: Date; intensity: number }[] = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(start);
      date.setDate(start.getDate() + w * 7 + d);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      const count = activityMap[key] ?? (activeDays.has(key) ? 1 : 0);
      // intensity: 0=none, 1=1-2, 2=3-5, 3=6-10, 4=11-20, 5=21+
      const intensity = count === 0 ? 0
        : count <= 2  ? 1
        : count <= 5  ? 2
        : count <= 10 ? 3
        : count <= 20 ? 4
        : 5;
      week.push({ date, intensity });
    }
    weeks.push(week);
  }
  return weeks;
}


// ── Props ─────────────────────────────────────────────────────────────
interface ProgressionPageProps {
  languageCode: LanguageCode;
  userProgress: UserProgress;
  currentStreak: number;
  onBack: () => void;
}

// ── Main component ────────────────────────────────────────────────────
const ProgressionPage: React.FC<ProgressionPageProps> = ({
  languageCode,
  userProgress,
  currentStreak,
  onBack,
}) => {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const [showAllWords, setShowAllWords] = useState(false);

  const xp          = getTotalXP([languageCode]);
  const globalXP    = getTotalXP([]);
  const streak      = currentStreak || getDailyStreak();
  const { current: lvl, next: nextLvl, progress: lvlProgress } = getLevel(globalXP, languageCode);

  const wordsLearned = useMemo(() => {
    return userProgress.recentlyLearnedWords?.filter(w => w.language === languageCode) ?? [];
  }, [userProgress, languageCode]);

  // Words with SRS data sorted by interval desc (most anchored first)
  const anchoredWords = useMemo(() => {
    return wordsLearned
      .map(w => ({
        ...w,
        interval: userProgress.wordProgress?.[w.word]?.interval ?? 0,
      }))
      .sort((a, b) => b.interval - a.interval);
  }, [wordsLearned, userProgress.wordProgress]);

  const visibleWords = showAllWords ? anchoredWords : anchoredWords.slice(0, 8);

  const activityGrid = useMemo(() => buildActivityGrid(languageCode), [languageCode]);

  const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  return (
    <div className="pgr-screen" data-theme={theme}>
      <FloatingBackButton onBack={onBack} position="top-left" variant="minimal" />

      {/* ── Title bar ── */}
      <div className="pgr-titlebar">
        <h1 className="pgr-titlebar__title">{t('profile.progression.label')}</h1>
      </div>

      {/* ── Stats card with decorative border ── */}
      <div className="pgr-stats-card">
        <div className="pgr-stats-card__border-top">
          {'◇ ◁ ▷ ◇ ◁ ▷ ◇ ◁ ▷ ◇ ◁ ▷ ◇ ◁ ▷ ◇ ◁ ▷ ◇'}
        </div>
        <div className="pgr-stats-card__body">
          <div className="pgr-stat">
            <span className="pgr-stat__icon">𓇶</span>
            <span className="pgr-stat__value">{streak}</span>
            <span className="pgr-stat__label">{t('progression.streakDays')}</span>
          </div>
          <div className="pgr-stat__divider" />
          <div className="pgr-stat">
            <span className="pgr-stat__icon">◉</span>
            <span className="pgr-stat__value">{anchoredWords.length}</span>
            <span className="pgr-stat__label">{t('progression.wordsAnchored')}</span>
          </div>
          <div className="pgr-stat__divider" />
          <div className="pgr-stat">
            <span className="pgr-stat__icon">◈</span>
            <span className="pgr-stat__value">{lvl.level}</span>
            <span className="pgr-stat__label">{t('progression.levelLabel', { name: lvl.name.toUpperCase() })}</span>
          </div>
        </div>
        <div className="pgr-stats-card__border-bot">
          {'▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽ ▽'}
        </div>
      </div>

      {/* ── XP bar ── */}
      <div className="pgr-xp">
        <div className="pgr-xp__row">
          <span className="pgr-xp__label"><EmojiText text={lvl.icon} /> {lvl.name}</span>
          <span className="pgr-xp__xp">{xp} XP</span>
          {nextLvl && <span className="pgr-xp__next"><EmojiText text={nextLvl.icon} /> {nextLvl.name}</span>}
        </div>
        <div className="pgr-xp__track">
          <motion.div
            className="pgr-xp__fill"
            initial={{ width: 0 }}
            animate={{ width: `${lvlProgress}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* ── Activity heatmap ── */}
      <div className="pgr-section">
        <div className="pgr-section__head">
          <h2 className="pgr-section__title">{t('progression.lastWeeks')}</h2>
        </div>

        <div className="pgr-heatmap">
          {/* Day labels */}
          <div className="pgr-heatmap__labels">
            {DAY_LABELS.map((d, i) => (
              <span key={i} className="pgr-heatmap__day-label">{d}</span>
            ))}
          </div>
          {/* Weeks */}
          <div className="pgr-heatmap__grid">
            {activityGrid.map((week, wi) => (
              <div key={wi} className="pgr-heatmap__col">
                {week.map((cell, di) => (
                  <div
                    key={di}
                    className={`pgr-heatmap__cell pgr-heatmap__cell--${cell.intensity}`}
                    title={`${cell.date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}`}
                  >
                    <span className="pgr-heatmap__sym">{heatSym(cell.intensity)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Intensity legend */}
        <div className="pgr-legend">
          <span className="pgr-legend__label">{t('progression.intensity')}</span>
          {[0,1,2,3,4,5].map(i => (
            <div key={i} className={`pgr-heatmap__cell pgr-heatmap__cell--${i} pgr-legend__cell`} />
          ))}
          <span className="pgr-legend__plus">+</span>
        </div>
      </div>

      {/* ── Anchored words ── */}
      <div className="pgr-section">
        <div className="pgr-section__head">
          <h2 className="pgr-section__title">{t('progression.anchoredWords')}</h2>
          {anchoredWords.length > 8 && (
            <button className="pgr-section__link" onClick={() => setShowAllWords(v => !v)}>
              {showAllWords ? t('progression.reduce') : t('progression.seeAll', { count: anchoredWords.length })}
            </button>
          )}
        </div>

        {anchoredWords.length === 0 ? (
          <p className="pgr-empty">{t('progression.noWordsYet')}</p>
        ) : (
          <div className="pgr-word-list">
            <AnimatePresence>
              {visibleWords.map((w, i) => (
                <motion.div
                  key={w.word}
                  className="pgr-word-row"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <span className="pgr-word-row__word">{w.word}</span>
                  <span className="pgr-word-row__trans">{getWordTranslation(w, i18n.language)}</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* ── Category breakdown ── */}
      {(() => {
        const byCategory: Record<string, number> = {};
        wordsLearned.forEach(w => {
          const cat = w.category ?? t('progression.otherCategory');
          byCategory[cat] = (byCategory[cat] ?? 0) + 1;
        });
        const entries = Object.entries(byCategory).sort((a, b) => b[1] - a[1]);
        if (entries.length === 0) return null;
        const total = wordsLearned.length || 1;
        return (
          <div className="pgr-section">
            <div className="pgr-section__head">
              <h2 className="pgr-section__title">{t('progression.byCategory')}</h2>
            </div>
            <div className="pgr-cats">
              {entries.map(([cat, count]) => (
                <div key={cat} className="pgr-cat-row">
                  <span className="pgr-cat-row__name">{cat}</span>
                  <div className="pgr-cat-row__bar-track">
                    <motion.div
                      className="pgr-cat-row__bar-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.round((count / total) * 100)}%` }}
                      transition={{ duration: 0.6, ease: 'easeOut' }}
                    />
                  </div>
                  <span className="pgr-cat-row__count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default ProgressionPage;
